import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoins, faChartLine, faUsers, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import { ThemeProvider, DefaultTheme } from 'styled-components';
import { RootState } from '../../redux/store';
import { SummaryStatsProps } from './types';
import {
  StatsGrid,
  StatCard,
  StatContent,
  StatInfo,
  StatLabel,
  StatValue,
  StatHelp,
  IconWrapper,
  ProgramStatusValue,
  ProgramStatusHelp,
} from './styles';

const SummaryStats: React.FC<SummaryStatsProps> = ({ customerLoyalties, config }) => {
  const { isDarkMode } = useSelector((state: RootState) => state.theme);

  const totalPoints = customerLoyalties.reduce(
    (sum, loyalty) => sum + parseFloat(String(loyalty.points || 0)),
    0
  );

  const totalSpent = customerLoyalties.reduce(
    (sum, loyalty) => sum + parseFloat(String(loyalty.totalSpent || 0)),
    0
  );

  const activeCustomers = customerLoyalties.filter(
    (loyalty) => parseFloat(String(loyalty.points || 0)) > 0
  ).length;

  const theme: DefaultTheme = {
    mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark',
  };

  return (
    <ThemeProvider theme={theme}>
      <StatsGrid>
        <StatCard>
        <StatContent>
          <StatInfo>
            <StatLabel>Total Points</StatLabel>
            <StatValue>{totalPoints.toLocaleString()}</StatValue>
            <StatHelp>
              <FontAwesomeIcon icon={faCoins} style={{ marginRight: 4 }} />
              Loyalty Points
            </StatHelp>
          </StatInfo>
          <IconWrapper
            $bgColor={isDarkMode ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.1)'}
            $color="#22c55e"
          >
            <FontAwesomeIcon icon={faCoins} style={{ fontSize: 20 }} />
          </IconWrapper>
        </StatContent>
      </StatCard>

      <StatCard theme={theme}>
        <StatContent>
          <StatInfo>
            <StatLabel>Total Spent</StatLabel>
            <StatValue>${totalSpent.toLocaleString()}</StatValue>
            <StatHelp>
              <FontAwesomeIcon icon={faChartLine} style={{ marginRight: 4 }} />
              Customer Spending
            </StatHelp>
          </StatInfo>
          <IconWrapper
            $bgColor={isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)'}
            $color="#3b82f6"
          >
            <FontAwesomeIcon icon={faChartLine} style={{ fontSize: 20 }} />
          </IconWrapper>
        </StatContent>
      </StatCard>

      <StatCard theme={theme}>
        <StatContent>
          <StatInfo>
            <StatLabel>Active Customers</StatLabel>
            <StatValue>{activeCustomers}</StatValue>
            <StatHelp>
              <FontAwesomeIcon icon={faUsers} style={{ marginRight: 4 }} />
              With Points
            </StatHelp>
          </StatInfo>
          <IconWrapper
            $bgColor={isDarkMode ? 'rgba(249, 115, 22, 0.2)' : 'rgba(249, 115, 22, 0.1)'}
            $color="#f97316"
          >
            <FontAwesomeIcon icon={faUsers} style={{ fontSize: 20 }} />
          </IconWrapper>
        </StatContent>
      </StatCard>

      <StatCard theme={theme}>
        <StatContent>
          <StatInfo>
            <StatLabel>Program Status</StatLabel>
            <StatValue>
              <ProgramStatusValue $isActive={config?.loyaltyEnabled || false} $isDarkMode={isDarkMode}>
                {config?.loyaltyEnabled ? 'Active' : 'Inactive'}
              </ProgramStatusValue>
            </StatValue>
            <ProgramStatusHelp $isActive={config?.loyaltyEnabled || false} $isDarkMode={isDarkMode}>
              <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: 4 }} />
              {config?.loyaltyEnabled ? 'Running' : 'Disabled'}
            </ProgramStatusHelp>
          </StatInfo>
          <IconWrapper
            $bgColor={
              config?.loyaltyEnabled
                ? isDarkMode
                  ? 'rgba(34, 197, 94, 0.2)'
                  : 'rgba(34, 197, 94, 0.1)'
                : isDarkMode
                ? 'rgba(160, 174, 192, 0.2)'
                : 'rgba(113, 128, 150, 0.1)'
            }
            $color={config?.loyaltyEnabled ? '#22c55e' : isDarkMode ? '#a0aec0' : '#718096'}
          >
            <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: 20 }} />
          </IconWrapper>
        </StatContent>
      </StatCard>
    </StatsGrid>
    </ThemeProvider>
  );
};

export default SummaryStats;
